import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ear, ListChecks, SlidersHorizontal, MessageCircle, Layers, Gauge } from 'lucide-react';
import { Button } from '../ds';
import { cx, disc } from '../ds/classes';
import { useAuth } from '../../context/AuthContext';
import { Page, Container, PageHeader, SectionHeading, Card, Steps } from './Page';
import { ExampleSummary, ExampleCard } from './ExampleAnalysis';

const STEPS = [
  { title: 'Drop a clip', text: 'A stem like your kick or vocal, or the whole mix, up to 10 minutes.' },
  { title: 'Get the read', text: "A score, what's working and what isn't, in plain words." },
  { title: 'Fix it in your DAW', text: 'One thing to fix next, with the exact plugin moves for your DAW.' },
  { title: 'Upload V2', text: 'See what improved and what to fix next, version by version.' },
];

// Where "start" buttons go: your projects once you're in, otherwise a free account
const useStartPath = () => (useAuth().isLoggedIn ? '/projects' : '/signup');

const Feature = ({ icon: Icon, title, children }) => (
  <Card>
    <span className={cx(disc, 'w-10 h-10 bg-raised text-ink-soft')}>
      <Icon size={20} strokeWidth={2.5} aria-hidden="true" />
    </span>
    <h3 className="m-0 font-display text-section text-ink">{title}</h3>
    <p className="m-0 text-body text-ink-soft">{children}</p>
  </Card>
);

const ClosingCall = ({ title, text, label }) => {
  const start = useStartPath();
  return (
    <div className="flex flex-col items-start gap-space-4 p-space-8 rounded-radius-xl bg-surface">
      <h2 className="m-0 font-display text-title-2 text-ink">{title}</h2>
      <p className="m-0 max-w-[640px] text-lead text-ink-soft">{text}</p>
      <Button as={Link} to={start} icon={ArrowRight} iconPosition="end">{label}</Button>
    </div>
  );
};

export const HomePage = () => {
  const start = useStartPath();
  return (
    <Page>
      <Container className="flex flex-col gap-space-14 py-space-10 sm:py-space-14">
        <section className="flex flex-wrap items-center gap-space-10">
          <div className="flex-[1_1_480px] min-w-0 flex flex-col gap-space-7">
            <span className="self-start px-space-4 py-space-2 rounded-radius-pill bg-surface text-label text-ink-soft">
              Sonic Insight · your second pair of ears
            </span>
            <h1 className="m-0 font-display font-extrabold text-[56px] leading-[0.92] tracking-[-0.04em] sm:text-[76px] lg:text-display-hero text-ink">
              Hear what your mix <span className="text-coral">needs.</span>
            </h1>
            <p className="m-0 max-w-[520px] text-[20px] leading-[1.5] text-ink-soft">
              Drop in a clip and get a plain-English read on what's working, what's muddy, and which plugins to
              reach for in the DAW you already use.
            </p>
            <div className="flex flex-wrap gap-space-3">
              <Button as={Link} to={start} size="lg" icon={ArrowRight} iconPosition="end">
                Analyze a clip free
              </Button>
              <Button as={Link} to="/sonic-insight" variant="secondary" size="lg">
                See an example
              </Button>
            </div>
            <span className="text-small text-muted">10 free analyses a day. Works with Ableton, FL Studio, Logic and more.</span>
          </div>
          <div className="flex-[1_1_420px] min-w-0">
            <ExampleSummary />
          </div>
        </section>

        <section aria-labelledby="how-it-works" className="flex flex-col gap-space-6">
          <SectionHeading id="how-it-works" title="How it works" />
          <Steps steps={STEPS} labelledBy="how-it-works" />
        </section>

        <section aria-labelledby="for-you" className="flex flex-col gap-space-6">
          <SectionHeading
            id="for-you"
            title="Built for producers who are still learning to mix"
            lead="You can hear that something's off. We tell you what it is and how to fix it, without making the decisions for you."
          />
          <div className="grid gap-space-4 md:grid-cols-3">
            <Feature icon={Ear} title="Plain words first">Low end that's tighter, a vocal that's buried. Numbers only where you'll type them in.</Feature>
            <Feature icon={ListChecks} title="One fix at a time">The single most useful next step, so you're never staring at a list of twenty problems.</Feature>
            <Feature icon={SlidersHorizontal} title="Your DAW's own plugins">Moves for EQ Eight, Fruity Parametric EQ or Channel EQ, with the settings to dial in.</Feature>
          </div>
        </section>

        <ClosingCall
          title="Ready for a second pair of ears?"
          text="Make a free account, drop in a clip and get your first read in about a minute."
          label="Analyze a clip free"
        />
      </Container>
    </Page>
  );
};

export const SonicInsightPage = () => (
  <Page>
    <Container className="flex flex-col gap-space-14 py-space-10 sm:py-space-14">
      <PageHeader
        eyebrow="Sonic Insight"
        title="A mixing coach that listens first"
        lead="Sonic Insight measures your clip, then explains what it hears like a friend who mixes: what's working, the one thing to fix next, and how to fix it in your DAW."
      />

      <section aria-labelledby="example" className="flex flex-col gap-space-6">
        <SectionHeading id="example" title="What an analysis looks like" lead="This is V2 of a full mix. Every version gets a score, what changed since the last one, and the next fix." />
        <div className="max-w-[820px] w-full">
          <ExampleCard />
        </div>
      </section>

      <section aria-labelledby="steps" className="flex flex-col gap-space-6">
        <SectionHeading id="steps" title="How it works" />
        <Steps steps={STEPS} labelledBy="steps" />
      </section>

      <section aria-labelledby="inside" className="flex flex-col gap-space-6">
        <SectionHeading id="inside" title="What's inside" />
        <div className="grid gap-space-4 md:grid-cols-3">
          <Feature icon={Layers} title="Stems and full mixes">Tag each clip: a kick, a lead vocal, or the full mix. Stems get a stem health score; a full mix gets a mastering readiness score.</Feature>
          <Feature icon={MessageCircle} title="A chat for every clip">Ask follow-up questions about that clip and how it sits with the rest of your project.</Feature>
          <Feature icon={Gauge} title="Full metering when you want it">Peaks, loudness, tone and stereo, measured for every version, one click away.</Feature>
        </div>
      </section>

      <ClosingCall title="Try it on your own mix" text="10 free analyses a day, no card needed." label="Analyze a clip free" />
    </Container>
  </Page>
);

export const ProductsPage = () => {
  const start = useStartPath();
  return (
    <Page>
      <Container className="flex flex-col gap-space-10 py-space-10 sm:py-space-14">
        <PageHeader title="Products" lead="Tools that guide your mix while you stay in control." />
        <div className="grid gap-space-4 md:grid-cols-2">
          <Card className="gap-space-4">
            <span className="self-start px-space-3 py-space-1 rounded-radius-pill bg-raised text-label text-ink-soft">Available now</span>
            <h2 className="m-0 font-display text-title-2 text-ink">Sonic Insight</h2>
            <p className="m-0 text-body text-ink-soft">
              Upload a stem or your full mix and get a score, what changed since your last version, and the next fix with
              plugin moves for your DAW.
            </p>
            <div className="flex flex-wrap gap-space-3 pt-space-2">
              <Button as={Link} to={start} icon={ArrowRight} iconPosition="end">Analyze a clip free</Button>
              <Button as={Link} to="/sonic-insight" variant="secondary">Learn more</Button>
            </div>
          </Card>
          <Card className="gap-space-4 bg-transparent border border-line">
            <span className="self-start px-space-3 py-space-1 rounded-radius-pill bg-surface text-label text-muted">Later</span>
            <h2 className="m-0 font-display text-title-2 text-ink-soft">More tools</h2>
            <p className="m-0 text-body text-muted">We're working on more ways to help you finish tracks. They'll show up here.</p>
          </Card>
        </div>
      </Container>
    </Page>
  );
};

export const AboutPage = () => (
  <Page>
    <Container className="flex flex-col gap-space-14 py-space-10 sm:py-space-14">
      <PageHeader
        eyebrow="About"
        title="Tools that guide you, not replace you"
        lead="The Producer's Friend is made by producers for producers. Technical know-how shouldn't stand between you and a finished track."
      />
      <div className="grid gap-space-6 lg:grid-cols-2 items-start">
        <div className="flex flex-col gap-space-4 text-lead text-ink-soft">
          <p className="m-0">
            Most of us learned to mix by guessing: turning knobs until it sounded less wrong. We build tools that turn
            measurements into advice you can act on, in words you already use.
          </p>
          <p className="m-0">
            Whether you're finishing your first beat or your fiftieth, the goal is the same: help you hear what your mix
            needs, then get out of the way so you can make it.
          </p>
        </div>
        <Card className="p-space-8">
          <span className="text-label text-muted">What we believe</span>
          <h2 className="m-0 font-display text-title-2 text-ink">Your ears, your call</h2>
          <p className="m-0 text-body text-ink-soft">
            The best music comes from human taste. Our AI doesn't mix your track for you. It gives you the context to make
            better decisions yourself.
          </p>
        </Card>
      </div>
      <section aria-labelledby="values" className="flex flex-col gap-space-6">
        <SectionHeading id="values" title="What matters to us" />
        <div className="grid gap-space-4 md:grid-cols-3">
          <Feature icon={Ear} title="Easy to start">Good feedback shouldn't need a big budget or a manual. Ten analyses a day are free.</Feature>
          <Feature icon={Gauge} title="Measured, then explained">Every tip starts from what we measured in your audio, not a guess.</Feature>
          <Feature icon={SlidersHorizontal} title="Fits how you work">Advice that fits your instrument, your DAW and what you're going for.</Feature>
        </div>
      </section>
      <ClosingCall title="Let's make something" text="Bring a clip you're stuck on." label="Analyze a clip free" />
    </Container>
  </Page>
);

const TERMS = [
  ['1. Acceptance of terms', [
    "By accessing and using The Producer's Friend, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services or tools.",
  ]],
  ['2. Use of tools', [
    'Our audio analysis tools are provided for creative and professional use. You agree not to:',
    ['Attempt to reverse engineer or decompile any of our proprietary algorithms.', 'Use the tools for any illegal or unauthorized purpose.', 'Interfere with or disrupt the integrity or performance of the tools.'],
  ]],
  ['3. Intellectual property', [
    "All content, tools, and algorithms on this site are the exclusive property of The Producer's Friend. Your use of the tools does not grant you ownership of any intellectual property rights in our services.",
  ]],
  ['4. Disclaimer of warranties', [
    'Our tools are provided "as is" without any warranty. While we strive for professional-grade precision, we do not guarantee that the analysis will be 100% accurate for every audio source or mixing environment.',
  ]],
  ['5. Limitation of liability', [
    "The Producer's Friend shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our tools.",
  ]],
  ['6. Changes to terms', [
    'We reserve the right to modify these terms at any time. We will notify users of any changes by updating the date at the bottom of this page.',
  ]],
];

export const TermsPage = () => (
  <Page>
    <Container className="flex flex-col gap-space-10 py-space-10 sm:py-space-14 max-w-[820px]">
      <PageHeader title="Terms of service" />
      <div className="flex flex-col gap-space-8 text-body text-ink-soft">
        {TERMS.map(([heading, parts]) => (
          <section key={heading} className="flex flex-col gap-space-3">
            <h2 className="m-0 font-display text-section text-ink">{heading}</h2>
            {parts.map((part, i) => (Array.isArray(part)
              ? <ul key={i} className="m-0 pl-space-6 list-disc flex flex-col gap-space-2">{part.map((item) => <li key={item}>{item}</li>)}</ul>
              : <p key={i} className="m-0">{part}</p>))}
          </section>
        ))}
        <p className="m-0 pt-space-6 border-t border-raised text-caption text-muted">Last updated: December 30, 2025</p>
      </div>
    </Container>
  </Page>
);
